/**
 * /close/templates — Reusable close checklist item templates.
 *
 * Templates are instantiated into close_items every time a new close
 * period is opened (via open_close_period). Editing a template here
 * affects *future* closes only — already-instantiated items live on
 * their own.
 *
 * If the entity has zero templates, opening a close auto-seeds the 15
 * defaults. "Seed defaults" is also exposed as a button here for the
 * "I accidentally deactivated them all, reset" recovery case.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { api, errorMessage } from "@/lib/api";

// ─── Types ────────────────────────────────────────────────────────────────

type Template = {
  id: string;
  entity_id: string;
  name: string;
  category: string;
  description: string | null;
  default_owner_id: string | null;
  due_days_before_target: number;
  is_blocking: boolean;
  sort_order: number;
  is_active: boolean;
  created_at: string;
};

const CATEGORY_OPTIONS = [
  { value: "reconciliation", label: "Reconciliation" },
  { value: "subledger", label: "Subledger tie-out" },
  { value: "je_review", label: "Journal review" },
  { value: "revenue", label: "Revenue" },
  { value: "depreciation", label: "Depreciation" },
  { value: "final", label: "Final review" },
];

// ─── Route ────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/_auth/close/templates")({
  component: TemplatesPage,
});

function TemplatesPage() {
  const qc = useQueryClient();
  const [toast, setToast] = useState<{ msg: string; severity: "success" | "error" | "info" } | null>(null);
  const [editTemplate, setEditTemplate] = useState<Template | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const tmplQuery = useQuery({
    queryKey: ["close", "templates"],
    queryFn: () => api<Template[]>("/api/close/templates"),
  });

  const seedDefaults = useMutation({
    mutationFn: () => api<Template[]>("/api/close/templates/seed", { method: "POST" }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["close", "templates"] });
      setToast({ msg: `Seeded ${r.length} default templates`, severity: "success" });
    },
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => api(`/api/close/templates/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["close", "templates"] });
      setToast({ msg: "Template deactivated (existing items unaffected)", severity: "info" });
    },
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });

  const templates = (tmplQuery.data ?? []).filter((t) => showInactive || t.is_active);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            component={Link}
            to={"/close" as never}
            sx={{ mb: 0.5 }}
          >
            Close calendar
          </Button>
          <Typography variant="h5">Close checklist templates</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
            Reusable items copied into every new close period. Edits here affect <strong>future</strong> closes only — items already instantiated keep their original definition.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={() => seedDefaults.mutate()}
            disabled={seedDefaults.isPending}
          >
            Seed defaults
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
          >
            Add template
          </Button>
        </Stack>
      </Stack>

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Switch
                size="small"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <Typography variant="body2">Show inactive</Typography>
            </Stack>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {templates.length} template{templates.length === 1 ? "" : "s"}
            </Typography>
          </Stack>

          {tmplQuery.isLoading ? (
            <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>
          ) : templates.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                No templates yet.
              </Typography>
              <Button
                variant="contained"
                startIcon={<RestartAltIcon />}
                onClick={() => seedDefaults.mutate()}
                disabled={seedDefaults.isPending}
                sx={{ mt: 1 }}
              >
                Seed the 15 standard templates
              </Button>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }}>#</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="center">Due (days before)</TableCell>
                  <TableCell align="center">Blocking</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates
                  .slice()
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map((t) => (
                    <TableRow key={t.id} hover sx={{ opacity: t.is_active ? 1 : 0.5 }}>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {t.sort_order}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{t.name}</Typography>
                        {t.description && (
                          <Typography variant="caption" color="text.secondary">{t.description}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={CATEGORY_OPTIONS.find((c) => c.value === t.category)?.label || t.category}
                        />
                      </TableCell>
                      <TableCell align="center">{t.due_days_before_target}</TableCell>
                      <TableCell align="center">
                        {t.is_blocking ? (
                          <Chip size="small" color="warning" label="blocking" variant="outlined" />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {t.is_active ? (
                          <Chip size="small" color="success" label="active" variant="outlined" />
                        ) : (
                          <Chip size="small" label="inactive" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => setEditTemplate(t)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {t.is_active && (
                          <Tooltip title="Deactivate">
                            <IconButton size="small" onClick={() => deactivate.mutate(t.id)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {(addOpen || editTemplate) && (
        <TemplateDialog
          template={editTemplate}
          onClose={() => { setAddOpen(false); setEditTemplate(null); }}
          onResult={(r) => setToast(r)}
        />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? <Alert severity={toast.severity}>{toast.msg}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
}

// ─── Dialog ───────────────────────────────────────────────────────────────

function TemplateDialog({
  template,
  onClose,
  onResult,
}: {
  template: Template | null;
  onClose: () => void;
  onResult: (r: { msg: string; severity: "success" | "error" | "info" }) => void;
}) {
  const qc = useQueryClient();
  const isEdit = template !== null;
  const [form, setForm] = useState({
    name: template?.name ?? "",
    category: template?.category ?? "reconciliation",
    description: template?.description ?? "",
    due_days_before_target: template?.due_days_before_target ?? 0,
    is_blocking: template?.is_blocking ?? true,
    sort_order: template?.sort_order ?? 999,
    is_active: template?.is_active ?? true,
  });

  const save = useMutation({
    mutationFn: () => {
      if (isEdit) {
        return api(`/api/close/templates/${template!.id}`, {
          method: "PATCH",
          body: form,
        });
      }
      return api("/api/close/templates", { method: "POST", body: form });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["close", "templates"] });
      onResult({ msg: isEdit ? "Template updated" : "Template added", severity: "success" });
      onClose();
    },
    onError: (e) => onResult({ msg: errorMessage(e), severity: "error" }),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Edit template" : "Add template"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Item name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            size="small"
            autoFocus
          />
          <TextField
            label="Category"
            select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            size="small"
          >
            {CATEGORY_OPTIONS.map((c) => (
              <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Description (optional)"
            multiline
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            size="small"
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Due (business days before target close)"
              type="number"
              value={form.due_days_before_target}
              onChange={(e) => setForm({ ...form, due_days_before_target: parseInt(e.target.value || "0", 10) })}
              size="small"
              helperText="0 = due on target date"
              fullWidth
              inputProps={{ min: 0, max: 10 }}
            />
            <TextField
              label="Sort order"
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || "0", 10) })}
              size="small"
              helperText="Lower = appears earlier"
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Checkbox
              checked={form.is_blocking}
              onChange={(e) => setForm({ ...form, is_blocking: e.target.checked })}
              size="small"
              sx={{ p: 0.5 }}
            />
            <Typography variant="body2">Blocking — must complete before sign-off</Typography>
          </Stack>
          {isEdit && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Checkbox
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                size="small"
                sx={{ p: 0.5 }}
              />
              <Typography variant="body2">Active — included in new close periods</Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!form.name.trim() || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Saving…" : isEdit ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
